import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useSeo } from '../utils/seo';

// Lazy-load the map (Leaflet bundle is heavy and only used when user toggles "Mapa")
const PropertyMap = lazy(() => import('../components/PropertyMap'));

// Lead form modal — replaces per-agent WhatsApp CTAs
import { LeadForm } from '../components/LeadForm';
import { 
  Search, Home, Building2, MapPin, Bath, Grid3X3, Maximize2, 
  ExternalLink, Bell, Menu, X, Calculator as CalculatorIcon,
  DollarSign, Loader2, TrendingDown, Heart, MessageCircle, Calendar,
  Phone, ArrowUpDown, Check, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { trackPageView, trackSearch, trackFavoriteAdd, trackScheduleVisit, trackContactClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://realestate-stats.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

const Properties = () => {
  useSeo({
    title: 'Buscador de Propiedades en CABA y Zona Norte | Vendetucasa.ar',
    description: 'Buscá propiedades en venta y alquiler en 67 barrios. Filtros exclusivos: oportunidades bajo precio de mercado, pozo con año de entrega, mapa interactivo.',
    canonical: 'https://vendetucasa.ar/propiedades',
    ogUrl: 'https://vendetucasa.ar/propiedades',
  });

  // Lead form modal (replaces WhatsApp): opens with the selected property context
  const [leadForm, setLeadForm] = useState({ open: false, property: null, type: 'info', title: null });
  const openLead = (property, type = 'info', title = null) => setLeadForm({ open: true, property, type, title });
  const closeLead = () => setLeadForm(s => ({ ...s, open: false }));
  // Last successful search cached in localStorage → instant first paint even on cold backend.
  const cacheKey = 'properties_last_search_v1';
  const cached = (() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const [properties, setProperties] = useState(cached?.properties || []);
  const [loading, setLoading] = useState(false);
  const [zones, setZones] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [totalFetched, setTotalFetched] = useState(cached?.totalFetched || 0);
  // Zone market info (real RE/MAX median + opportunity threshold)
  const [zoneInfo, setZoneInfo] = useState(cached?.zoneInfo || null);
  
  // Favorites
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('property_favorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [showSaveFavoritesModal, setShowSaveFavoritesModal] = useState(false);
  const [saveFavoritesForm, setSaveFavoritesForm] = useState({ email: '', whatsapp: '', method: 'email' });
  
  // Visit scheduling
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [selectedPropertyForVisit, setSelectedPropertyForVisit] = useState(null);
  
  // Basic filters
  // Initialize filter state from URL query params (e.g., /propiedades?zone=Belgrano)
  const [searchParams] = useSearchParams();
  const [operation, setOperation] = useState(() => searchParams.get('operation') || 'venta');
  const [propertyType, setPropertyType] = useState(() => searchParams.get('property_type') || 'departamento');
  // Multi-zone selection. Supports `zones=A,B,C` (preferred) and legacy `zone=A`.
  const [selectedZones, setSelectedZones] = useState(() => {
    const multi = searchParams.get('zones');
    if (multi) {
      return multi.split(',').map(z => z.trim()).filter(Boolean);
    }
    const single = searchParams.get('zone');
    return single ? [single] : ['Palermo'];
  });
  // UI state for the multi-select dropdown
  const [zoneDropdownOpen, setZoneDropdownOpen] = useState(false);
  const [zoneSearch, setZoneSearch] = useState('');
  
  // Advanced filters (now always visible)
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [minM2, setMinM2] = useState('');
  const [maxM2, setMaxM2] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [maxPricePerM2, setMaxPricePerM2] = useState('');
  
  // Sorting
  const [sortBy, setSortBy] = useState('default');
  
  // Opportunities-only toggle (filter properties priced 30%+ below zone average)
  const [opportunitiesOnly, setOpportunitiesOnly] = useState(false);
  // Pozo / Desarrollo toggle (filter properties in construction or pre-sale)
  const [devOnly, setDevOnly] = useState(false);
  // Delivery year chips (e.g., ['ready', '2026', '2028', '2029+']). Empty = no chip filter.
  const [selectedDeliveryYears, setSelectedDeliveryYears] = useState([]);
  // View mode: 'list' (grid of cards) or 'map' (Leaflet)
  const [viewMode, setViewMode] = useState('list');
  
  // Alert form
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [alertForm, setAlertForm] = useState({
    email: '',
    name: '',
    budget_min: '',
    budget_max: '',
    property_type: 'departamento',
    rooms_min: '',
  });

  // Room options
  const roomOptions = [
    { value: 1, label: 'Mono' },
    { value: 2, label: '2 Amb' },
    { value: 3, label: '3 Amb' },
    { value: 4, label: '4 Amb' },
    { value: 5, label: '5+' },
  ];

  // Load zones and property types
  useEffect(() => {
    const loadOptions = async () => {
      try {
        console.log('Loading options from:', API);
        const [zonesRes, typesRes] = await Promise.all([
          axios.get(`${API}/properties/zones`),
          axios.get(`${API}/properties/types`)
        ]);
        console.log('Zones loaded:', zonesRes.data.zones?.length);
        console.log('Types loaded:', typesRes.data.types?.length);
        setZones(zonesRes.data.zones || []);
        setPropertyTypes(typesRes.data.types || []);
      } catch (error) {
        console.error('Error loading options:', error);
        console.error('API URL was:', API);
        toast.error('Error al cargar opciones de búsqueda');
      }
    };
    loadOptions();
  }, []);

  // Auto-disable "Solo Oportunidades" if user switches to alquiler (only valid for venta)
  useEffect(() => {
    if (operation === 'alquiler' && opportunitiesOnly) {
      setOpportunitiesOnly(false);
    }
  }, [operation, opportunitiesOnly]);

  // Auto-disable "Solo Pozo / Desarrollo" if property_type is not departamento/PH
  useEffect(() => {
    if (devOnly && propertyType !== 'departamento' && propertyType !== 'ph' && propertyType !== 'all') {
      setDevOnly(false);
    }
  }, [propertyType, devOnly]);

  // Clear delivery-year chips when "Solo Pozo" is turned off (they only make sense together)
  useEffect(() => {
    if (!devOnly && selectedDeliveryYears.length > 0) {
      setSelectedDeliveryYears([]);
    }
  }, [devOnly, selectedDeliveryYears.length]);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('property_favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Search properties
  const searchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        operation, 
        property_type: propertyType, 
        zones: selectedZones.join(','),
        limit: 60 
      };
      
      if (selectedRooms.length > 0) {
        params.rooms = selectedRooms.join(',');
      }
      if (minM2) params.min_m2 = parseFloat(minM2);
      if (maxM2) params.max_m2 = parseFloat(maxM2);
      if (minPrice) params.min_price = parseInt(minPrice);
      if (maxPrice) params.max_price = parseInt(maxPrice);
      if (maxPricePerM2) params.max_price_per_m2 = parseFloat(maxPricePerM2);
      // When "Solo Oportunidades" is on, force ascending price/m² so the best deals show first.
      const effectiveSort = opportunitiesOnly && (!sortBy || sortBy === 'default') ? 'price_m2_asc' : sortBy;
      if (effectiveSort && effectiveSort !== 'default') params.sort_by = effectiveSort;
      if (opportunitiesOnly) params.opportunities_only = true;
      if (devOnly) params.dev_only = true;
      if (selectedDeliveryYears.length > 0) params.delivery_years = selectedDeliveryYears.join(',');
      
      const response = await axios.get(`${API}/properties/search`, { params });
      setProperties(response.data.properties);
      setTotalFetched(response.data.total_fetched || response.data.count);
      setZoneInfo(response.data.zone_info || null);

      // Persist last successful search to localStorage for instant next-load
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          properties: response.data.properties || [],
          totalFetched: response.data.total_fetched || response.data.count || 0,
          zoneInfo: response.data.zone_info || null,
          ts: Date.now(),
        }));
      } catch { /* quota exceeded — ignore */ }
      
      // Track search event
      trackSearch({
        zone: selectedZones.join(','),
        property_type: propertyType,
        operation,
        rooms: selectedRooms.join(','),
        min_price: minPrice,
        max_price: maxPrice
      });
      
      if (response.data.message) {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Error searching properties:', error);
      toast.error('Error al buscar propiedades');
    } finally {
      setLoading(false);
    }
  }, [operation, propertyType, selectedZones, selectedRooms, minM2, maxM2, minPrice, maxPrice, maxPricePerM2, sortBy, opportunitiesOnly, devOnly, selectedDeliveryYears]);

  // Initial search on mount
  useEffect(() => {
    searchProperties();
  }, [searchProperties]);

  // Handle room selection toggle
  const toggleRoom = (roomValue) => {
    setSelectedRooms(prev => 
      prev.includes(roomValue) 
        ? prev.filter(r => r !== roomValue)
        : [...prev, roomValue]
    );
  };

  // Toggle favorite
  const toggleFavorite = (property) => {
    setFavorites(prev => {
      const isFav = prev.some(f => f.id === property.id);
      if (isFav) {
        return prev.filter(f => f.id !== property.id);
      } else {
        // Track adding to favorites
        trackFavoriteAdd(property);
        return [...prev, property];
      }
    });
  };

  const isFavorite = (propertyId) => favorites.some(f => f.id === propertyId);

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedRooms([]);
    setMinM2('');
    setMaxM2('');
    setMinPrice('');
    setMaxPrice('');
    setMaxPricePerM2('');
    setSortBy('default');
    setOpportunitiesOnly(false);
    setDevOnly(false);
    setSelectedDeliveryYears([]);
  };

  // Handle alert subscription
  const handleAlertSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/subscriptions/alerts`, {
        ...alertForm,
        budget_min: alertForm.budget_min ? parseInt(alertForm.budget_min) : null,
        budget_max: alertForm.budget_max ? parseInt(alertForm.budget_max) : null,
        rooms_min: alertForm.rooms_min ? parseInt(alertForm.rooms_min) : null,
      });
      toast.success('¡Te avisaremos cuando haya propiedades que coincidan!');
      setShowAlertForm(false);
      setAlertForm({ email: '', name: '', budget_min: '', budget_max: '', property_type: 'departamento', rooms_min: '' });
    } catch (error) {
      toast.error('Error al crear la alerta');
    }
  };

  // Handle save favorites — via lead form (unified capture)
  const handleSaveFavorites = () => {
    // Compose a message with the favorites list so it's captured with the lead
    const favList = favorites.map((f, i) => `${i+1}. ${f.title} - USD ${f.price?.toLocaleString()}`).join('\n');
    openLead(
      null,
      'info',
      'Guardar mis favoritos',
    );
    // Prefill message via a side-effect after opening — small hack: store in state and pass down.
    // Simpler: just open the form; the user can paste details in the message field.
    setShowSaveFavoritesModal(false);
    // Toast so they know why the form opened
    if (favList) toast.info(`${favorites.length} favoritos listos para consultar`);
  };

  // Handle schedule visit — opens the neutral lead form (no more WhatsApp to Ivan)
  const handleScheduleVisit = (property) => {
    trackScheduleVisit(property);
    trackContactClick('lead_form', '/propiedades');
    openLead(property, 'visita', 'Agendar visita');
  };

  const formatPrice = (price, currency = 'USD') => {
    if (!price) return 'Consultar';
    const cur = (currency || 'USD').toUpperCase();
    if (cur === 'ARS') {
      // For ARS we show "$ X" with locale formatting (no decimals — rentals are usually round numbers)
      return `$ ${Math.round(price).toLocaleString('es-AR')}`;
    }
    return `USD ${price.toLocaleString()}`;
  };

  // Group zones by region
  const groupedZones = zones.reduce((acc, z) => {
    if (!acc[z.region]) acc[z.region] = [];
    acc[z.region].push(z);
    return acc;
  }, {});

  // Multi-select helpers
  const zoneDropdownRef = useRef(null);
  useEffect(() => {
    if (!zoneDropdownOpen) return;
    const onClickOutside = (e) => {
      if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(e.target)) {
        setZoneDropdownOpen(false);
        setZoneSearch('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [zoneDropdownOpen]);

  const toggleZone = (zoneName) => {
    setSelectedZones(prev => {
      if (prev.includes(zoneName)) {
        // Don't allow deselecting the last one — there must always be at least one
        if (prev.length === 1) return prev;
        return prev.filter(z => z !== zoneName);
      }
      return [...prev, zoneName];
    });
  };
  const clearZonesExcept = (zoneName) => setSelectedZones([zoneName]);

  // Filter zones by search term inside the dropdown
  const normalize = (s) => (s || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filteredGroupedZones = Object.entries(groupedZones).reduce((acc, [region, regionZones]) => {
    const q = normalize(zoneSearch);
    const filtered = q ? regionZones.filter(z => normalize(z.label).includes(q)) : regionZones;
    if (filtered.length > 0) acc[region] = filtered;
    return acc;
  }, {});

  const hasActiveFilters = selectedRooms.length > 0 || minM2 || maxM2 || minPrice || maxPrice || maxPricePerM2 || opportunitiesOnly;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm shadow-sm sticky top-0 z-50 border-b border-blue-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="relative">
                <svg viewBox="0 0 100 100" className="w-10 h-10">
                  <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
                  <circle cx="50" cy="50" r="12" fill="#003DA5"/>
                  <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="#003DA5"/>
                  <circle cx="25" cy="25" r="5" fill="white"/>
                </svg>
              </div>
              <span className="text-2xl font-bold">
                <span className="text-[#E31C23]">vende</span>
                <span className="text-slate-600">tu</span>
                <span className="text-[#E31C23]">casa</span>
                <span className="text-[#003DA5]">.ar</span>
              </span>
            </Link>
            
            <button
              className="md:hidden p-2 text-[#003DA5]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <nav className="hidden md:flex space-x-5 items-center">
              <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Sobre Mí</Link>
              <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Servicios</Link>
              <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Calculadora</Link>
              <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Barrios</Link>
              <Link to="/propiedades" className="text-[#E31C23] font-medium">Propiedades</Link>
              <Link to="/noticias" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium">Noticias</Link>
              
              {/* Favorites Button */}
              <button
                onClick={() => setShowFavoritesModal(true)}
                className="relative p-2 text-[#E31C23] hover:bg-red-50 rounded-full transition-colors"
                data-testid="favorites-button"
              >
                <Heart className={`w-5 h-5 ${favorites.length > 0 ? 'fill-current' : ''}`} />
                {favorites.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E31C23] text-white text-xs rounded-full flex items-center justify-center">
                    {favorites.length}
                  </span>
                )}
              </button>
              
              <Button asChild size="sm" className="bg-[#E31C23] hover:bg-[#C11A1F] text-white">
                <Link to="/#appraisal-form">Conocé el Valor de tu Casa</Link>
              </Button>
            </nav>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-blue-100 py-4">
            <nav className="container mx-auto px-4 flex flex-col space-y-3">
              <Link to="/#about" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Sobre Mí</Link>
              <Link to="/#services" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Servicios</Link>
              <Link to="/calculadora" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Calculadora</Link>
              <Link to="/barrios" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Barrios</Link>
              <Link to="/propiedades" className="text-[#E31C23] font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Propiedades</Link>
              <Link to="/noticias" className="text-[#003DA5] hover:text-[#E31C23] transition-colors font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Noticias</Link>
              <button
                onClick={() => { setShowFavoritesModal(true); setMobileMenuOpen(false); }}
                className="flex items-center gap-2 text-[#E31C23] py-2"
              >
                <Heart className={`w-5 h-5 ${favorites.length > 0 ? 'fill-current' : ''}`} />
                Favoritos ({favorites.length})
              </button>
              <Button asChild className="bg-[#E31C23] hover:bg-[#C11A1F] text-white w-full mt-2">
                <Link to="/#appraisal-form" onClick={() => setMobileMenuOpen(false)}>Conocé el Valor de tu Casa</Link>
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Search Section - ALL FILTERS VISIBLE */}
      <section className="bg-gradient-to-r from-[#003DA5] to-[#0051D5] py-6">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <h1 className="text-xl md:text-2xl font-bold text-white mb-1">
              Buscador de Propiedades
            </h1>
            <p className="text-blue-200 text-sm">
              {zones.length} barrios disponibles · CABA y Zona Norte
            </p>
          </div>
          
          {/* All Filters - Always Visible */}
          <div className="max-w-6xl mx-auto bg-white rounded-xl p-4 md:p-5 shadow-xl">
            {/* Row 1: Basic Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Operación</Label>
                <Select value={operation} onValueChange={setOperation}>
                  <SelectTrigger data-testid="select-operation" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Comprar o alquilar</SelectItem>
                    <SelectItem value="venta">Comprar</SelectItem>
                    <SelectItem value="alquiler">Alquilar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Tipo</Label>
                <Select value={propertyType} onValueChange={setPropertyType}>
                  <SelectTrigger data-testid="select-property-type" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    {propertyTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-2 md:col-span-2">
                <Label className="text-xs text-slate-500 mb-1 block">
                  Barrios {selectedZones.length > 1 && (
                    <span className="text-[#003DA5] font-semibold">({selectedZones.length} seleccionados)</span>
                  )}
                </Label>
                <div className="relative" ref={zoneDropdownRef}>
                  {/* Trigger button (looks like a Select) showing chips inside */}
                  <button
                    type="button"
                    onClick={() => setZoneDropdownOpen(o => !o)}
                    className="w-full min-h-9 px-3 py-1.5 text-left border border-slate-300 rounded-md bg-white hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#003DA5]/40 transition flex flex-wrap items-center gap-1"
                    data-testid="zones-multiselect-trigger"
                    aria-expanded={zoneDropdownOpen}
                  >
                    {selectedZones.length === 0 ? (
                      <span className="text-sm text-slate-400">Seleccioná barrios...</span>
                    ) : (
                      selectedZones.slice(0, 3).map(z => (
                        <span
                          key={z}
                          className="inline-flex items-center gap-1 bg-[#003DA5]/10 text-[#003DA5] text-xs font-medium px-2 py-0.5 rounded-full"
                          data-testid={`zone-chip-${z}`}
                        >
                          {z}
                          {selectedZones.length > 1 && (
                            <X
                              className="w-3 h-3 hover:text-[#E31C23] cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); toggleZone(z); }}
                              data-testid={`zone-chip-remove-${z}`}
                            />
                          )}
                        </span>
                      ))
                    )}
                    {selectedZones.length > 3 && (
                      <span className="inline-flex items-center bg-slate-200 text-slate-700 text-xs px-2 py-0.5 rounded-full">
                        +{selectedZones.length - 3} más
                      </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-500 ml-auto transition-transform ${zoneDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown panel */}
                  {zoneDropdownOpen && (
                    <div
                      className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-xl max-h-[360px] overflow-hidden flex flex-col"
                      data-testid="zones-multiselect-panel"
                    >
                      {/* Search input */}
                      <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                          <Input
                            type="text"
                            placeholder="Buscar barrio..."
                            value={zoneSearch}
                            onChange={(e) => setZoneSearch(e.target.value)}
                            className="h-8 pl-7 text-sm"
                            data-testid="zones-search-input"
                            autoFocus
                          />
                        </div>
                        {selectedZones.length > 1 && (
                          <div className="flex justify-between items-center mt-2 text-xs">
                            <span className="text-slate-500">{selectedZones.length} seleccionados</span>
                            <button
                              type="button"
                              onClick={() => clearZonesExcept(selectedZones[0])}
                              className="text-[#E31C23] hover:underline"
                              data-testid="zones-clear-extra"
                            >
                              Dejar solo {selectedZones[0]}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Scrollable list */}
                      <div className="overflow-y-auto flex-1">
                        {Object.entries(filteredGroupedZones).length === 0 ? (
                          <p className="p-4 text-center text-sm text-slate-400">Sin resultados</p>
                        ) : (
                          Object.entries(filteredGroupedZones).map(([region, regionZones]) => (
                            <div key={region}>
                              <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">
                                {region} ({regionZones.length})
                              </div>
                              {regionZones.map(z => {
                                const isSelected = selectedZones.includes(z.value);
                                return (
                                  <button
                                    key={z.value}
                                    type="button"
                                    onClick={() => toggleZone(z.value)}
                                    className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 transition text-left ${isSelected ? 'bg-[#003DA5]/5 font-medium text-[#003DA5]' : 'text-slate-700'}`}
                                    data-testid={`zone-option-${z.value}`}
                                  >
                                    <span>{z.label}</span>
                                    <div className={`w-4 h-4 border rounded flex items-center justify-center ${isSelected ? 'bg-[#003DA5] border-[#003DA5]' : 'border-slate-300'}`}>
                                      {isSelected && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ))
                        )}
                      </div>

                      {/* Footer */}
                      <div className="p-2 border-t border-slate-100 bg-slate-50 flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setZoneDropdownOpen(false); setZoneSearch(''); }}
                          className="text-xs h-7"
                          data-testid="zones-multiselect-done"
                        >
                          Listo
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Row 2: Rooms Selection */}
            <div className="mb-4">
              <Label className="text-xs text-slate-500 mb-2 block">Ambientes</Label>
              <div className="flex flex-wrap gap-2">
                {roomOptions.map(room => (
                  <button
                    key={room.value}
                    onClick={() => toggleRoom(room.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      selectedRooms.includes(room.value)
                        ? 'bg-[#003DA5] text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                    data-testid={`room-filter-${room.value}`}
                  >
                    {room.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Row 3: Price and Area Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">M² Mín</Label>
                <Input
                  type="number"
                  placeholder="30"
                  value={minM2}
                  onChange={(e) => setMinM2(e.target.value)}
                  className="h-9"
                  data-testid="min-m2-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">M² Máx</Label>
                <Input
                  type="number"
                  placeholder="150"
                  value={maxM2}
                  onChange={(e) => setMaxM2(e.target.value)}
                  className="h-9"
                  data-testid="max-m2-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Precio Mín (USD)</Label>
                <Input
                  type="number"
                  placeholder="50000"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="h-9"
                  data-testid="min-price-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Precio Máx (USD)</Label>
                <Input
                  type="number"
                  placeholder="200000"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="h-9"
                  data-testid="max-price-input"
                />
              </div>
            </div>
            
            {/* OPPORTUNITY FINDER - Highlighted Section */}
            <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-green-800 text-sm">🔥 Buscador de Oportunidades</h3>
                    <p className="text-xs text-green-600">Encontrá propiedades por debajo del precio de mercado</p>
                  </div>
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <Label className="text-xs text-green-700 whitespace-nowrap font-medium">Precio/m² máximo:</Label>
                  <div className="relative flex-1 max-w-[180px]">
                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                    <Input
                      type="number"
                      placeholder="ej: 2000"
                      value={maxPricePerM2}
                      onChange={(e) => setMaxPricePerM2(e.target.value)}
                      className="h-9 pl-7 border-green-400 focus:border-green-600 bg-white"
                      data-testid="max-price-per-m2-input"
                    />
                  </div>
                  <span className="text-xs text-green-600 font-medium">USD/m²</span>
                </div>
              </div>
              {maxPricePerM2 && (
                <p className="text-xs text-green-700 mt-2 bg-green-100 px-2 py-1 rounded inline-block">
                  ✓ Buscando propiedades a menos de USD {parseInt(maxPricePerM2).toLocaleString()}/m² · Resultados ordenados por oportunidad
                </p>
              )}

              {/* Solo Oportunidades toggle: filters listings 30%+ below the zone median.
                  Only meaningful for SALES — disabled (with explanation) when operation is "alquiler"
                  because the zone avg/threshold is computed from USD sale listings. */}
              <div className={`mt-3 pt-3 border-t border-green-200 flex flex-wrap items-center justify-between gap-2 ${operation === 'alquiler' ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">⭐</span>
                  <div>
                    <p className="text-xs font-semibold text-green-800">Solo Oportunidades</p>
                    <p className="text-[11px] text-green-600">
                      {operation === 'alquiler'
                        ? 'Disponible solo para Venta — el promedio de mercado se calcula sobre propiedades en venta'
                        : selectedZones.length === 1
                          ? `Mostrar solo propiedades 30% o más bajo el promedio de ${selectedZones[0]}`
                          : `Mostrar solo propiedades 30% o más bajo el promedio de su propio barrio`}
                      {operation !== 'alquiler' && selectedZones.length === 1 && zoneInfo?.opportunity_threshold_m2
                        ? ` (menos de USD ${Math.round(zoneInfo.opportunity_threshold_m2).toLocaleString()}/m²)`
                        : ''}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={opportunitiesOnly}
                  disabled={operation === 'alquiler'}
                  onClick={() => operation !== 'alquiler' && setOpportunitiesOnly(prev => !prev)}
                  data-testid="opportunities-only-toggle"
                  className={`relative inline-flex h-6 w-11 shrink-0 ${operation === 'alquiler' ? 'cursor-not-allowed' : 'cursor-pointer'} rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 ${opportunitiesOnly ? 'bg-green-600' : 'bg-slate-300'}`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${opportunitiesOnly ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>
              {opportunitiesOnly && (
                <p className="text-xs text-green-800 mt-2 bg-green-200 px-2 py-1 rounded inline-block font-medium" data-testid="opportunities-only-active-banner">
                  ⭐ Filtro de oportunidades activo · Solo propiedades 30%+ bajo el promedio del barrio
                </p>
              )}

              {/* Pozo / Desarrollo toggle. Only meaningful for departamento/PH (not casa or terrenos). */}
              {(() => {
                const devApplicable = propertyType === 'departamento' || propertyType === 'ph' || propertyType === 'all';
                return (
                  <div className={`mt-3 pt-3 border-t border-green-200 flex flex-wrap items-center justify-between gap-2 ${!devApplicable ? 'opacity-60' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🏗️</span>
                      <div>
                        <p className="text-xs font-semibold text-green-800">Solo Pozo / Desarrollo</p>
                        <p className="text-[11px] text-green-600">
                          {devApplicable
                            ? 'Propiedades en construcción, preventa o con entrega futura (2026-2030)'
                            : 'Disponible solo para Departamento o PH'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={devOnly}
                      disabled={!devApplicable}
                      onClick={() => devApplicable && setDevOnly(prev => !prev)}
                      data-testid="dev-only-toggle"
                      className={`relative inline-flex h-6 w-11 shrink-0 ${!devApplicable ? 'cursor-not-allowed' : 'cursor-pointer'} rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${devOnly ? 'bg-blue-600' : 'bg-slate-300'}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${devOnly ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                );
              })()}
              {devOnly && (
                <p className="text-xs text-blue-800 mt-2 bg-blue-100 px-2 py-1 rounded inline-block font-medium" data-testid="dev-only-active-banner">
                  🏗️ Filtro Pozo activo · Solo propiedades en construcción o preventa
                </p>
              )}

              {/* Delivery-year chip filters — appear only when "Solo Pozo" is active */}
              {devOnly && (() => {
                const currentYear = new Date().getFullYear();
                const buckets = [
                  { key: 'ready', label: `A estrenar ${currentYear}` },
                  { key: String(currentYear + 1), label: `Entrega ${currentYear + 1}` },
                  { key: String(currentYear + 2), label: `Entrega ${currentYear + 2}` },
                  { key: String(currentYear + 3), label: `Entrega ${currentYear + 3}` },
                  { key: `${currentYear + 3}+`, label: `${currentYear + 3}+` },
                ];
                const toggleBucket = (k) => {
                  setSelectedDeliveryYears(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
                };
                return (
                  <div className="mt-3 pt-3 border-t border-blue-200" data-testid="delivery-years-chips">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-semibold text-blue-700 mr-1">Estado / Año de entrega:</span>
                      {buckets.map(b => {
                        const active = selectedDeliveryYears.includes(b.key);
                        return (
                          <button
                            key={b.key}
                            type="button"
                            onClick={() => toggleBucket(b.key)}
                            data-testid={`delivery-year-chip-${b.key}`}
                            className={`text-xs px-3 py-1 rounded-full border transition-colors ${active
                              ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
                              : 'bg-white text-blue-700 border-blue-300 hover:border-blue-500 hover:bg-blue-50'}`}
                          >
                            {active && '✓ '}{b.label}
                          </button>
                        );
                      })}
                      {selectedDeliveryYears.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedDeliveryYears([])}
                          className="text-[11px] text-slate-500 hover:text-[#E31C23] underline ml-1"
                          data-testid="delivery-years-clear"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-blue-600 mt-1.5">
                      {selectedDeliveryYears.length === 0
                        ? 'Mostrando todas las propiedades pozo. Filtrá por año para ver entregas específicas.'
                        : `Filtrando por ${selectedDeliveryYears.length} estado(s) seleccionado(s)`}
                    </p>
                  </div>
                );
              })()}
            </div>
            
            {/* Row 4: Search Button and Actions */}
            <div className="flex flex-wrap gap-2 items-center justify-between">
              <div className="flex gap-2">
                <Button 
                  onClick={searchProperties} 
                  className="bg-[#E31C23] hover:bg-[#C11A1F]"
                  disabled={loading}
                  data-testid="search-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Buscar Propiedades
                </Button>
                
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearAllFilters} className="text-slate-600">
                    Limpiar
                  </Button>
                )}
              </div>
              
              <Button 
                variant="outline" 
                onClick={() => setShowAlertForm(!showAlertForm)}
                className="border-[#003DA5] text-[#003DA5]"
                data-testid="create-alert-button"
              >
                <Bell className="w-4 h-4 mr-2" />
                Crear Alerta
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-6">
        <div className="container mx-auto px-4">
          {/* Results Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <div>
              <h2 className="text-lg font-bold text-[#003DA5]">
                {(() => {
                  const typeLabel = propertyType === 'all'
                    ? 'Propiedades'
                    : (propertyTypes.find(t => t.value === propertyType)?.label || 'Propiedades');
                  const opLabel = operation === 'all'
                    ? ''
                    : (operation === 'venta' ? ' en Venta' : ' en Alquiler');
                  const zonesLabel = selectedZones.length === 1
                    ? selectedZones[0]
                    : selectedZones.length <= 3
                      ? selectedZones.join(', ')
                      : `${selectedZones.slice(0, 2).join(', ')} y ${selectedZones.length - 2} barrios más`;
                  return `${typeLabel}${opLabel} en ${zonesLabel}`;
                })()}
              </h2>
              <p className="text-sm text-slate-600">
                {properties.length} propiedades 
                {totalFetched > properties.length && ` (filtradas de ${totalFetched})`}
                {maxPricePerM2 && <span className="text-green-600 ml-2">· Filtro oportunidades activo</span>}
                {opportunitiesOnly && <span className="text-green-700 ml-2 font-medium">· ⭐ Solo Oportunidades</span>}
                {devOnly && <span className="text-blue-700 ml-2 font-medium">· 🏗️ Solo Pozo</span>}
              </p>
            </div>
            
            {/* View mode toggle + Sort */}
            <div className="flex items-center gap-3">
              <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="view-mode-toggle">
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 text-xs font-medium transition ${viewMode === 'list' ? 'bg-[#003DA5] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid="view-mode-list"
                >
                  📋 Lista
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('map')}
                  className={`px-3 py-1.5 text-xs font-medium transition ${viewMode === 'map' ? 'bg-[#003DA5] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid="view-mode-map"
                >
                  🗺️ Mapa
                </button>
              </div>
              <ArrowUpDown className="w-4 h-4 text-slate-500" />
              <Select value={sortBy} onValueChange={(value) => { setSortBy(value); }}>
                <SelectTrigger className="w-[200px] h-9" data-testid="sort-select">
                  <SelectValue placeholder="Ordenar por..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Relevancia</SelectItem>
                  <SelectItem value="price_m2_asc">Precio/m² ↑ (Menor)</SelectItem>
                  <SelectItem value="price_m2_desc">Precio/m² ↓ (Mayor)</SelectItem>
                  <SelectItem value="price_asc">Precio ↑ (Menor)</SelectItem>
                  <SelectItem value="price_desc">Precio ↓ (Mayor)</SelectItem>
                  <SelectItem value="area_asc">Superficie ↑ (Menor)</SelectItem>
                  <SelectItem value="area_desc">Superficie ↓ (Mayor)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Alert Form */}
          {showAlertForm && (
            <Card className="mb-6 border-[#003DA5] border-2" data-testid="alert-form-card">
              <CardContent className="p-4">
                <h3 className="text-lg font-bold text-[#003DA5] mb-3 flex items-center">
                  <Bell className="w-5 h-5 mr-2" />
                  Crear Alerta de Propiedades
                </h3>
                <form onSubmit={handleAlertSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input 
                    placeholder="Tu nombre"
                    value={alertForm.name}
                    onChange={(e) => setAlertForm({...alertForm, name: e.target.value})}
                    required
                  />
                  <Input 
                    type="email"
                    placeholder="tu@email.com"
                    value={alertForm.email}
                    onChange={(e) => setAlertForm({...alertForm, email: e.target.value})}
                    required
                  />
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-[#003DA5] flex-1">Activar</Button>
                    <Button type="button" variant="outline" onClick={() => setShowAlertForm(false)}>Cancelar</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Loading State — skeleton grid only on cold first paint (no cached data).
              If we have cached/previous results, keep them visible while refreshing. */}
          {loading && properties.length === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="properties-skeleton-grid">
              {Array.from({ length: 8 }).map((_, idx) => (
                <Card key={`skel-${idx}`} className="overflow-hidden animate-pulse">
                  <div className="h-40 bg-slate-200" />
                  <CardContent className="p-3 space-y-2">
                    <div className="h-3 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                    <div className="flex gap-2 pt-1">
                      <div className="h-3 bg-slate-200 rounded w-10" />
                      <div className="h-3 bg-slate-200 rounded w-10" />
                      <div className="h-3 bg-slate-200 rounded w-10" />
                    </div>
                    <div className="h-8 bg-slate-200 rounded mt-2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Refreshing indicator — when refetching but we still have results to show */}
          {loading && properties.length > 0 && (
            <div className="flex items-center justify-center mb-3 gap-2 text-xs text-slate-500" data-testid="properties-refreshing-indicator">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Actualizando resultados...</span>
            </div>
          )}

          {/* Property Cards Grid — render cached/loaded properties (visible during refresh too) */}
          {properties.length > 0 && viewMode === 'list' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="properties-grid">
              {properties.map((property, index) => (
                <Card key={property.id || index} className="overflow-hidden hover:shadow-xl transition-all group" data-testid={`property-card-${index}`}>
                  {/* Property Image */}
                  <div className="relative h-40 bg-slate-200">
                    {property.images && property.images.length > 0 ? (
                      <img 
                        src={property.images[0]} 
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=600';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100">
                        <Home className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    
                    {/* Favorite Button */}
                    <button
                      onClick={(e) => { e.preventDefault(); toggleFavorite(property); }}
                      className={`absolute top-2 right-2 p-2 rounded-full transition-all ${
                        isFavorite(property.id) 
                          ? 'bg-[#E31C23] text-white' 
                          : 'bg-white/80 text-slate-600 hover:bg-white hover:text-[#E31C23]'
                      }`}
                      data-testid={`favorite-btn-${index}`}
                    >
                      <Heart className={`w-4 h-4 ${isFavorite(property.id) ? 'fill-current' : ''}`} />
                    </button>
                    
                    {/* Operation Badge (top-left) — useful when filter shows both venta + alquiler */}
                    {operation === 'all' && property.operation && (
                      <div className="absolute top-2 left-2">
                        <Badge
                          className={`text-xs px-2 py-0.5 ${property.operation === 'venta' ? 'bg-[#003DA5]' : 'bg-amber-600'} text-white`}
                          data-testid={`property-operation-badge-${index}`}
                        >
                          {property.operation === 'venta' ? 'Venta' : 'Alquiler'}
                        </Badge>
                      </div>
                    )}

                    {/* DEVELOPMENT BADGE — 🏗️ POZO / A ESTRENAR. Always visible when detected,
                        regardless of the dev_only filter. Shows delivery year when known. */}
                    {property.is_development && (() => {
                      const cy = new Date().getFullYear();
                      const yr = property.year_built || property.delivery_year;
                      // Label & tooltip
                      let label, tip;
                      if (yr === cy) {
                        label = '✨ A ESTRENAR';
                        tip = `Terminado / a estrenar ${cy}`;
                      } else if (yr && yr > cy) {
                        label = `🏗️ POZO ${yr}`;
                        tip = `Entrega estimada: ${yr}`;
                      } else {
                        label = '🏗️ POZO';
                        tip = 'Propiedad en construcción o preventa';
                      }
                      return (
                        <div
                          className={`absolute right-2 ${operation === 'all' && property.operation ? 'top-9' : 'top-2'}`}
                        >
                          <Badge
                            className={`${yr === cy ? 'bg-amber-500 hover:bg-amber-600 ring-amber-300' : 'bg-blue-600 hover:bg-blue-700 ring-blue-300'} text-white text-xs px-2 py-1 font-bold shadow-lg ring-2`}
                            title={tip}
                            data-testid={`property-dev-badge-${index}`}
                          >
                            {label}
                          </Badge>
                        </div>
                      );
                    })()}

                    {/* OPPORTUNITY BADGE — only meaningful for SALE listings in USD
                        (avg_price_m2 is computed from USD sale listings; comparing it to
                        rental price/m² produces nonsensical "-99%" discounts). */}
                    {(() => {
                      if (!property.price_per_m2) return null;
                      const currency = (property.currency || 'USD').toUpperCase();
                      if (currency !== 'USD') return null;
                      const propOp = property.operation || operation;
                      if (propOp !== 'venta') return null;
                      // Prefer per-zone breakdown (multi-zone aware); fall back to aggregated values.
                      const zoneStats = zoneInfo?.by_zone?.[property.zone] || null;
                      const propAvg = zoneStats?.avg_price_m2 ?? zoneInfo?.avg_price_m2;
                      const propThreshold = zoneStats?.opportunity_threshold_m2 ?? zoneInfo?.opportunity_threshold_m2;
                      if (!propAvg || !propThreshold || property.price_per_m2 >= propThreshold) return null;
                      const discount = Math.round((1 - property.price_per_m2 / propAvg) * 100);
                      return (
                        <div className={`absolute ${operation === 'all' && property.operation ? 'top-9' : 'top-2'} left-2`}>
                          <Badge
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 font-bold shadow-lg ring-2 ring-green-300 animate-pulse"
                            title={`Promedio de ${property.zone}: USD ${Math.round(propAvg).toLocaleString()}/m². Esta propiedad está un ${discount}% por debajo.`}
                            data-testid={`property-opportunity-flag-${index}`}
                          >
                            🔥 OPORTUNIDAD -{discount}%
                          </Badge>
                        </div>
                      );
                    })()}

                    {/* Price Badge — currency-aware (USD vs ARS) */}
                    <div className="absolute bottom-2 left-2">
                      <Badge className="bg-[#003DA5] text-white text-sm px-2 py-1">
                        {formatPrice(property.price, property.currency)}
                      </Badge>
                    </div>
                    
                    {/* Price per m2 Badge — currency-aware; "OPORTUNIDAD" coloring only for SALE listings in USD */}
                    {property.price_per_m2 && (() => {
                      const currency = (property.currency || 'USD').toUpperCase();
                      const propOp = property.operation || operation;
                      const zoneStats = zoneInfo?.by_zone?.[property.zone] || null;
                      const propAvg = zoneStats?.avg_price_m2 ?? zoneInfo?.avg_price_m2;
                      const propThreshold = zoneStats?.opportunity_threshold_m2 ?? zoneInfo?.opportunity_threshold_m2;
                      // Opportunity styling only when listing is USD and operation is venta (zone avg is venta-USD-based).
                      const isOpportunity = currency === 'USD' && propOp === 'venta' && propThreshold && property.price_per_m2 < propThreshold;
                      const prefix = currency === 'ARS' ? 'ARS' : 'USD';
                      const value = currency === 'ARS'
                        ? Math.round(property.price_per_m2).toLocaleString('es-AR')
                        : property.price_per_m2.toLocaleString();
                      return (
                        <div className="absolute bottom-2 right-2">
                          <Badge
                            className={`text-xs px-1.5 py-0.5 ${isOpportunity ? 'bg-green-600' : 'bg-slate-600'} text-white`}
                            title={isOpportunity
                              ? `Oportunidad: 30%+ por debajo del promedio de ${property.zone} (USD ${Math.round(propAvg).toLocaleString()}/m²)`
                              : propOp === 'venta' && propAvg
                                ? `Promedio de ${property.zone}: USD ${Math.round(propAvg).toLocaleString()}/m²`
                                : undefined}
                            data-testid={isOpportunity ? 'property-opportunity-badge' : 'property-price-m2-badge'}
                          >
                            {prefix} {value}/m²
                          </Badge>
                        </div>
                      );
                    })()}
                  </div>
                  
                  <CardContent className="p-3">
                    {/* Title */}
                    <h3 className="font-medium text-sm text-slate-800 line-clamp-1 mb-1">{property.title}</h3>
                    
                    {/* Address */}
                    <div className="flex items-start gap-1 mb-2">
                      <MapPin className="w-3 h-3 text-[#E31C23] mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-slate-500 line-clamp-1">{property.address || 'Consultar dirección'}</p>
                    </div>
                    
                    {/* Features */}
                    <div className="flex gap-3 mb-3 text-xs text-slate-600">
                      {property.rooms && (
                        <span className="flex items-center gap-1">
                          <Grid3X3 className="w-3 h-3" /> {property.rooms} amb
                        </span>
                      )}
                      {property.total_area && (
                        <span className="flex items-center gap-1">
                          <Maximize2 className="w-3 h-3" /> {property.total_area} m²
                        </span>
                      )}
                      {property.bathrooms && (
                        <span className="flex items-center gap-1">
                          <Bath className="w-3 h-3" /> {property.bathrooms}
                        </span>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button asChild size="sm" className="flex-1 bg-[#E31C23] hover:bg-[#C11A1F] text-xs h-8">
                        <a href={property.url} target="_blank" rel="noopener noreferrer">
                          Ver Detalle <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-green-500 text-green-600 hover:bg-green-50 text-xs h-8 px-2"
                        onClick={() => handleScheduleVisit(property)}
                        data-testid={`visit-btn-${index}`}
                      >
                        <MessageCircle className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Map View */}
          {properties.length > 0 && viewMode === 'map' && (
            <Suspense fallback={
              <div className="flex items-center justify-center h-[70vh] bg-slate-100 rounded-xl text-slate-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Cargando mapa...
              </div>
            }>
              <PropertyMap properties={properties} />
            </Suspense>
          )}

          {/* No Results */}
          {!loading && properties.length === 0 && (
            <div className="text-center py-12" data-testid="no-results">
              <div className="bg-white rounded-xl p-8 max-w-2xl mx-auto shadow-lg">
                <Building2 className="w-16 h-16 mx-auto text-[#003DA5] mb-4" />
                <h3 className="text-xl font-bold text-[#003DA5] mb-2">
                  No se encontraron propiedades
                </h3>
                <p className="text-slate-600 mb-6 text-sm">
                  {hasActiveFilters 
                    ? 'Probá ajustando los filtros para ver más resultados. Podés cambiar la zona, el rango de precios o la cantidad de ambientes.'
                    : selectedZones.length === 1
                      ? `No hay propiedades disponibles en ${selectedZones[0]}. Probá seleccionando otra zona o ajustando los filtros.`
                      : `No hay propiedades disponibles en los barrios seleccionados. Probá agregando otros barrios o ajustando los filtros.`
                  }
                </p>
                
                {hasActiveFilters && (
                  <Button onClick={clearAllFilters} variant="outline" className="border-[#E31C23] text-[#E31C23]">
                    Limpiar Filtros
                  </Button>
                )}
                
                <div className="border-t pt-6 mt-6">
                  <p className="text-sm text-slate-500 mb-3">¿No encontrás lo que buscás?</p>
                  <Button onClick={() => setShowAlertForm(true)} variant="outline" className="border-[#003DA5] text-[#003DA5]">
                    <Bell className="w-4 h-4 mr-2" />
                    Crear alerta personalizada
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Footer note */}
          {!loading && properties.length > 0 && (
            <div className="text-center mt-6">
              <p className="text-xs text-slate-500">
                Datos de propiedades actualizados · Powered by RE/MAX Argentina
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Favorites Modal */}
      <Dialog open={showFavoritesModal} onOpenChange={setShowFavoritesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#E31C23]" />
              Mis Favoritos ({favorites.length})
            </DialogTitle>
            <DialogDescription>
              Propiedades que guardaste para ver más tarde
            </DialogDescription>
          </DialogHeader>
          
          {favorites.length === 0 ? (
            <div className="text-center py-8">
              <Heart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Todavía no tenés favoritos</p>
              <p className="text-xs text-slate-400 mt-1">Hacé click en el corazón de las propiedades que te interesen</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {favorites.map((fav, i) => (
                  <div key={fav.id} className="flex gap-3 p-2 bg-slate-50 rounded-lg">
                    <img src={fav.images?.[0]} alt="" className="w-16 h-16 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{fav.title}</p>
                      <p className="text-xs text-slate-500">{fav.address}</p>
                      <p className="text-sm font-bold text-[#003DA5]">USD {fav.price?.toLocaleString()}</p>
                    </div>
                    <button onClick={() => toggleFavorite(fav)} className="text-slate-400 hover:text-[#E31C23]">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button 
                  onClick={() => { setShowFavoritesModal(false); setShowSaveFavoritesModal(true); }}
                  className="flex-1 bg-[#003DA5]"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Guardar y que me contacten
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Save Favorites Modal */}
      <Dialog open={showSaveFavoritesModal} onOpenChange={setShowSaveFavoritesModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Guardar Favoritos</DialogTitle>
            <DialogDescription>
              Dejanos tu contacto y te ayudamos a coordinar visitas
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveFavorites} className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={saveFavoritesForm.method === 'email' ? 'default' : 'outline'}
                onClick={() => setSaveFavoritesForm({...saveFavoritesForm, method: 'email'})}
                className="flex-1"
              >
                Email
              </Button>
              <Button
                type="button"
                variant={saveFavoritesForm.method === 'whatsapp' ? 'default' : 'outline'}
                onClick={() => setSaveFavoritesForm({...saveFavoritesForm, method: 'whatsapp'})}
                className="flex-1"
              >
                WhatsApp
              </Button>
            </div>
            
            {saveFavoritesForm.method === 'email' ? (
              <Input
                type="email"
                placeholder="tu@email.com"
                value={saveFavoritesForm.email}
                onChange={(e) => setSaveFavoritesForm({...saveFavoritesForm, email: e.target.value})}
                required
              />
            ) : (
              <Input
                type="tel"
                placeholder="11 5874 1111"
                value={saveFavoritesForm.whatsapp}
                onChange={(e) => setSaveFavoritesForm({...saveFavoritesForm, whatsapp: e.target.value})}
                required
              />
            )}
            
            <Button type="submit" className="w-full bg-[#E31C23]">
              {saveFavoritesForm.method === 'whatsapp' ? (
                <>
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Enviar por WhatsApp
                </>
              ) : (
                'Guardar y contactar'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead capture form modal (replaces the old WhatsApp-to-agent flow) */}
      <LeadForm
        open={leadForm.open}
        onClose={closeLead}
        defaultLeadType={leadForm.type}
        title={leadForm.title}
        property={leadForm.property}
        sourcePage="/propiedades"
      />

      {/* Footer */}
      <footer className="bg-[#003DA5] text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-3">
            <svg viewBox="0 0 100 100" className="w-10 h-10">
              <path d="M50 10 L90 45 L90 90 L10 90 L10 45 Z" fill="#E31C23"/>
              <circle cx="50" cy="50" r="12" fill="white"/>
              <path d="M50 55 Q35 70 35 85 L65 85 Q65 70 50 55" fill="white"/>
              <circle cx="25" cy="25" r="5" fill="white"/>
            </svg>
            <span className="text-xl font-bold">vendetucasa.ar</span>
          </Link>
          <p className="text-blue-200 text-sm">
            Datos de propiedades actualizados a diario
          </p>
          <p className="text-xs text-blue-300 mt-2">
            © {new Date().getFullYear()} Vendetucasa.ar
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Properties;
